/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { toNumber, uniqBy } from 'lodash';
import {
  Badge,
  Text,
  Table,
  TableContainer,
  TableHeaderCell,
  TableHeaderRow,
  TableHeader,
  TableBodyRow,
  TableBody,
  TableBodyCell,
  Stack,
} from '@cimpress-ui/react';
import { Option, InvalidConsideredOption, RoutingReceiptOptionData, Problem } from 'types';
import { IconRefresh } from '@cimpress-ui/react/icons';

type TableData = {
  nodeName: string;
  isValid: boolean;
  showInvalidationReason: boolean;
  cause?: string;
  wasSelected: boolean;
  hideRank?: boolean;
  rank?: string;
  ifThenId?: string;
  ifThenName?: string;
  isNodeAction?: boolean;
};

export const RoutingReceiptSummary = ({
  routingReceiptData,
  option,
  isPickedOption,
}: {
  routingReceiptData: RoutingReceiptOptionData;
  option: Option | InvalidConsideredOption;
  isPickedOption: boolean;
}) => {
  let showRankColumn = false;
  let showConditionGroupColumn = false;
  let addedStartingState = false;
  let currentRank: string | undefined;
  let lastNodeId: string;
  let addedNodeHeader = false;

  const tableData: TableData[] = [];

  let optionStartedValid = true;
  uniqBy(
    routingReceiptData.optionNodeValidity,
    (originalONV) =>
      `${originalONV.nodeId}-${originalONV.rank}-${originalONV.isValid}-${originalONV.ifThenId}-${originalONV.isNodeAction}`,
  ).forEach((onv) => {
    if (onv.nodeId !== lastNodeId) {
      lastNodeId = onv.nodeId;
      addedNodeHeader = false;
    }

    const currentNode = routingReceiptData.nodeData.find((node) => node.nodeId === onv.nodeId);

    // Add the starting state if we haven't already
    if (currentNode?.isStart && currentNode?.optionStates && !addedStartingState) {
      const optionStateAtBeginning = currentNode.optionStates.find((os) => os.optionId === option.optionId);
      if (optionStateAtBeginning) {
        optionStartedValid = optionStateAtBeginning.isValid;

        currentRank = optionStartedValid ? '1' : undefined;

        tableData.push({
          nodeName: 'Starting State',
          isValid: optionStartedValid,
          showInvalidationReason: true,
          cause: option.problem && findRelevantProblemDetail(option.problem),
          wasSelected: false,
          rank: currentRank,
        });
      }
      addedStartingState = true;
    }

    if (onv.rank) {
      showRankColumn = true;
      currentRank = onv.rank;
    }

    if (!optionStartedValid || !onv.isValid) {
      currentRank = undefined;
    }

    if (onv.ifThenId) {
      showConditionGroupColumn = true;
      if (!addedNodeHeader) {
        tableData.push({
          nodeName: currentNode?.name || onv.nodeId,
          wasSelected: false,
          isValid: optionStartedValid ? onv.isValid : optionStartedValid,
          showInvalidationReason: optionStartedValid,
          hideRank: true,
        });
        addedNodeHeader = true;
      }

      tableData.push({
        nodeName: currentNode?.name || onv.nodeId,
        ifThenId: onv.ifThenId,
        ifThenName: onv.ifThenName,
        isValid: optionStartedValid ? onv.isValid : optionStartedValid,
        showInvalidationReason: optionStartedValid,
        cause: onv.cause || (option.problem && findRelevantProblemDetail(option.problem)),
        wasSelected: false,
        rank: currentRank,
      });
    } else {
      tableData.push({
        nodeName: currentNode?.name || onv.nodeId,
        isValid: optionStartedValid ? onv.isValid : optionStartedValid,
        showInvalidationReason: optionStartedValid,
        cause: onv.cause || (option.problem && findRelevantProblemDetail(option.problem)),
        wasSelected: false,
        rank: currentRank,
        isNodeAction: onv.isNodeAction,
      });
    }

    if (!onv.isValid) {
      optionStartedValid = false;
    }
  });

  tableData.push({
    nodeName: 'Ending State',
    isValid: option.isValid,
    showInvalidationReason: false,
    wasSelected: isPickedOption,
    rank: isPickedOption ? '1' : currentRank ? `${toNumber(currentRank) + 1}` : undefined,
  });

  const columns = [
    {
      header: 'Node',
      accessor: 'nodeName',
    },
  ];

  if (showConditionGroupColumn) {
    columns.push({
      header: 'Condition Group',
      accessor: 'ifThenName',
    });
  }

  if (showRankColumn) {
    columns.push({
      header: 'Rank',
      accessor: 'rank',
    });
  }

  columns.push({
    header: 'Option Status',
    accessor: 'isValid',
  });

  return (
    <TableContainer>
      <Table aria-label="Routing Detail Summary Table">
        <TableHeader>
          <TableHeaderRow>
            {columns.map((column) => (
              <TableHeaderCell columnKey={column.header}>{column.header}</TableHeaderCell>
            ))}
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {tableData.map((dataRow, rowIndex) => (
            <TableBodyRow key={`receipts${rowIndex}`}>
              <TableBodyCell columnKey="Node">{nodeName(dataRow, showConditionGroupColumn)}</TableBodyCell>
              {showConditionGroupColumn && (
                <TableBodyCell columnKey="ConditionGroup">{ifThenInfo(dataRow)}</TableBodyCell>
              )}
              {showRankColumn && <TableBodyCell columnKey="Rank">{dataRow.rank}</TableBodyCell>}
              <TableBodyCell columnKey="OptionStatus">{optionStatus(dataRow)}</TableBodyCell>
            </TableBodyRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const conditionGroupContinuationStyle = css`
  display: flex;
  align-items: center;
`;

const findRelevantProblemDetail = (optionProblem: Problem): string => {
  if (optionProblem.causes?.length) {
    if (!optionProblem.type) {
      return optionProblem.causes[0].detail;
    }

    const causeWithType = optionProblem.causes.find((c) => c.type === optionProblem.type);
    if (causeWithType?.detail) {
      return causeWithType.detail;
    }
  }

  return optionProblem.type ? (optionProblem.type.split('/').at(-1) as string) : optionProblem.detail;
};

const ifThenInfo = (data: TableData) => {
  if (data.ifThenName) {
    return (
      <div>
        <Stack gap={8} direction="horizontal">
          <div css={conditionGroupContinuationStyle}>
            <IconRefresh size={16} />
          </div>
          <Text as="p">{data.ifThenName} (+Condition Group Continuation)</Text>
        </Stack>
      </div>
    );
  } else if (data.ifThenId) {
    return data.ifThenId;
  } else if (data.isNodeAction) {
    return "Node's Action";
  }
  return '';
};

const nodeName = (data: TableData, showConditionGroupColumn: boolean) => {
  if (showConditionGroupColumn && data.ifThenId) {
    return '';
  } else if (showConditionGroupColumn && data.isNodeAction) {
    return '';
  }
  return data.nodeName;
};

const optionStatus = (data: TableData) => {
  if (data.hideRank) {
    return '';
  }
  if (data.isValid) {
    return data.wasSelected ? <Badge tone="success">Selected</Badge> : <Badge tone="info">Available</Badge>;
  }
  return data.showInvalidationReason ? (
    <Badge tone="critical">{data.cause || 'Invalid'}</Badge>
  ) : (
    <Badge tone="critical">Invalid</Badge>
  );
};
